import {
  isNotEmpty,
  isNotNull,
  OrganizationDetailFragment,
  WorkLocationFragment,
} from '@alice-financial/alice-sdk'
import { Box, Button, Card, FormHelperText, Grid, styled, Typography } from '@alice-financial/pretext-ui'
import DownloadIcon from '@mui/icons-material/Download'
import React from 'react'
import { AssignPaygroupsButton } from '../homepage/PaygroupList'
import { CreateEditWorkLocationButton } from './WorkLocationForm'
import { WorkLocationLogoUploadButton } from './WorkLocationLogoUploadButton'

const Column = styled(Grid)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  flexWrap: 'nowrap',
  flexDirection: 'column',
}))

type WorkLocationItemProps = {
  org: OrganizationDetailFragment
  location: WorkLocationFragment
}

export const WorkLocationItem: React.FC<WorkLocationItemProps> = ({ org, location }) => {
  const { line1, line2, cityName, stateCode, zipcode } = location.address || {}
  const enrollmentMaterialsZipUrl = location?.enrollmentMaterials?.zippedMaterialsUrl
  const displayName = location.name
  const displayLogo = location.logo?.url || org.logo?.url
  const stateAndZip = [stateCode, zipcode].filter(isNotNull).join(' ')
  const addressLine1 = [line1 !== displayName ? line1 : null, line2].filter(isNotEmpty).join(', ')
  const addressLine2 = [cityName, stateAndZip].filter(isNotEmpty).join(', ')

  const payGroups = location.employersInfo || []

  return (
    <>
      <Card component={Grid} flexWrap="nowrap" container spacing={2} justifyContent="space-between">
        <Grid
          flexWrap="nowrap"
          container
          size={{ xs: 2 }}
          alignSelf="stretch"
          alignItems="center"
          justifyContent="center"
          sx={{ backgroundColor: 'rgba(128, 128, 128, 0.1)' }}
        >
          {displayLogo ? (
            <Box
              component="img"
              src={displayLogo}
              alt={`Logo for ${displayName}`}
              maxHeight={100}
              maxWidth="90%"
              mx="5%"
              sx={{
                boxShadow: (theme) => theme.shadows[1],
              }}
            />
          ) : (
            <WorkLocationLogoUploadButton org={org} location={location} />
          )}
        </Grid>
        <Column container flexGrow="1">
          <Grid flexGrow="1">
            <Typography variant="caption" component="p" fontWeight="bold" gutterBottom>
              {displayName}
            </Typography>
            <Typography variant="caption" component="p">
              {addressLine1 && (
                <>
                  {addressLine1}
                  <br />
                </>
              )}
              {addressLine2}
            </Typography>
          </Grid>
          <Grid>
            <CreateEditWorkLocationButton org={org} location={location} disableLookup />
          </Grid>
        </Column>
        <Column container size={{ xs: 3 }}>
          <Grid flexGrow="1">
            <Typography variant="caption" component="p" fontWeight="bold" gutterBottom>
              Pay group{payGroups.length > 1 ? 's' : ''}
            </Typography>
            <ul>
              {payGroups.length > 0 &&
                payGroups.map((payGroup) => (
                  <Typography variant="caption" component="li" key={payGroup.id}>
                    {payGroup.name}
                  </Typography>
                ))}
            </ul>
          </Grid>
          <Grid>
            <AssignPaygroupsButton editable location={location} />
          </Grid>
        </Column>
        <Column container size={{ xs: 3 }}>
          <Grid flexGrow="1">
            <Typography variant="caption" fontWeight="bold" gutterBottom>
              Enrollment materials
            </Typography>
            {!enrollmentMaterialsZipUrl && ( // find possible reasons that materials are not available
              <>
                {!displayLogo && <FormHelperText>Logo required</FormHelperText>}
                {!payGroups.length && <FormHelperText>Pay group required</FormHelperText>}
                {payGroups.length > 0 && !org.payrollConnection.payrollConnectedAt && (
                  <FormHelperText>Payroll connection must be confirmed</FormHelperText>
                )}
              </>
            )}
          </Grid>
          <Grid>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<DownloadIcon />}
              component="a"
              href={enrollmentMaterialsZipUrl || '#'}
              download
              disabled={!enrollmentMaterialsZipUrl}
            >
              Download
            </Button>
          </Grid>
        </Column>
      </Card>
    </>
  )
}
